export type GridCoord<RowId extends string = string, ColId extends string = string> = {
  rowId: RowId;
  colId: ColId;
};

export type GridMove =
  | 'up'
  | 'down'
  | 'shiftLeft'
  | 'shiftRight'
  | 'tab'
  | 'shiftTab';

type GridNavigationParams<RowId extends string, ColId extends string> = {
  rows: RowId[];
  cols: ColId[];
  current: GridCoord<RowId, ColId>;
  move: GridMove;
  wrap: boolean;
  isNavigable: (rowId: RowId, colId: ColId) => boolean;
};

const mod = (n: number, m: number): number => ((n % m) + m) % m;

export function getNextGridCoord<RowId extends string, ColId extends string>(
  params: GridNavigationParams<RowId, ColId>
): GridCoord<RowId, ColId> | null {
  const { rows, cols, current, move, wrap, isNavigable } = params;
  if (rows.length === 0 || cols.length === 0) return null;

  const r0 = rows.indexOf(current.rowId);
  const c0 = cols.indexOf(current.colId);
  if (r0 === -1 || c0 === -1) return null;

  const maxSteps = rows.length * cols.length;

  const step = (r: number, c: number): { r: number; c: number } | null => {
    if (move === 'up' || move === 'down') {
      const dr = move === 'up' ? -1 : 1;
      const nextR = r + dr;
      if (!wrap && (nextR < 0 || nextR >= rows.length)) return null;
      return { r: wrap ? mod(nextR, rows.length) : nextR, c };
    }

    if (move === 'shiftLeft' || move === 'shiftRight') {
      const dc = move === 'shiftLeft' ? -1 : 1;
      const nextC = c + dc;
      if (!wrap && (nextC < 0 || nextC >= cols.length)) return null;
      return { r, c: wrap ? mod(nextC, cols.length) : nextC };
    }

    if (move === 'tab' || move === 'shiftTab') {
      const dir = move === 'tab' ? 1 : -1;
      const linear = r * cols.length + c;
      const nextLinear = linear + dir;
      if (!wrap && (nextLinear < 0 || nextLinear >= rows.length * cols.length)) return null;
      const wrapped = wrap ? mod(nextLinear, rows.length * cols.length) : nextLinear;
      return {
        r: Math.floor(wrapped / cols.length),
        c: wrapped % cols.length,
      };
    }

    return null;
  };

  let r = r0;
  let c = c0;

  for (let i = 0; i < maxSteps; i++) {
    const next = step(r, c);
    if (!next) return null;
    r = next.r;
    c = next.c;
    const rowId = rows[r];
    const colId = cols[c];
    if (isNavigable(rowId, colId)) {
      return { rowId, colId };
    }
  }

  return null;
}

